import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboardAPI } from '../services/api';
import { 
  Users, 
  GraduationCap, 
  UserCog, 
  BookOpen,
  Loader2,
  UserCircle
} from 'lucide-react';

const DashboardHome = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => dashboardAPI.getStats(),
  });

  const stats = data?.data?.stats;
  const recentStudents = data?.data?.recent_students || [];
  const recentTeachers = data?.data?.recent_teachers || [];
  const topGroups = data?.data?.top_groups || [];

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <Loader2 className="spinner" size={40} />
        <p>Yuklanmoqda...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-error">
        Ma'lumotlarni yuklashda xatolik yuz berdi
      </div>
    );
  }

  return (
    <>
      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue">
            <GraduationCap size={28} />
          </div>
          <div className="stat-info">
            <h3>{stats?.total_students || 0}</h3>
            <p>Jami talabalar</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon green">
            <UserCog size={28} />
          </div>
          <div className="stat-info">
            <h3>{stats?.total_teachers || 0}</h3>
            <p>O'qituvchilar</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon orange">
            <Users size={28} />
          </div>
          <div className="stat-info">
            <h3>{stats?.total_groups || 0}</h3>
            <p>Guruhlar</p>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon purple">
            <BookOpen size={28} />
          </div>
          <div className="stat-info">
            <h3>{stats?.total_subjects || 0}</h3>
            <p>Fanlar soni</p>
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="content-grid">
        {/* Recent Students */}
        <div className="card">
          <div className="card-header">
            <h3>So'nggi qo'shilgan talabalar</h3>
            <span className="badge badge-blue">{recentStudents.length} ta</span>
          </div>
          <div className="card-body">
            {recentStudents.length === 0 ? (
              <div className="empty-state">
                <UserCircle size={48} />
                <p>Hali talabalar qo'shilmagan</p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Talaba</th>
                      <th>ID</th>
                      <th>Guruh</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentStudents.map((student) => (
                      <tr key={student.id}>
                        <td>
                          <div className="user-item">
                            <div className="user-item-avatar">
                              {getInitials(student.user?.first_name, student.user?.last_name)}
                            </div>
                            <div className="user-item-info">
                              <div className="name">{student.user?.full_name}</div>
                            </div>
                          </div>
                        </td>
                        <td>{student.student_id}</td>
                        <td>
                          <span className="badge badge-green">
                            {student.group_display || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Recent Teachers */}
        <div className="card">
          <div className="card-header">
            <h3>O'qituvchilar</h3>
            <span className="badge badge-green">{recentTeachers.length} ta</span>
          </div>
          <div className="card-body">
            {recentTeachers.length === 0 ? (
              <div className="empty-state">
                <UserCircle size={48} />
                <p>Hali o'qituvchilar qo'shilmagan</p>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>O'qituvchi</th>
                      <th>Fanlar</th>
                      <th>Tajriba</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTeachers.map((teacher) => (
                      <tr key={teacher.id}>
                        <td>
                          <div className="user-item">
                            <div className="user-item-avatar">
                              {getInitials(teacher.user?.first_name, teacher.user?.last_name)}
                            </div>
                            <div className="user-item-info">
                              <div className="name">{teacher.user?.full_name}</div>
                            </div>
                          </div>
                        </td>
                        <td>{teacher.subjects?.length || 0} ta fan</td>
                        <td>{teacher.experience_years} yil</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Groups */}
      {topGroups.length > 0 && (
        <div className="card" style={{ marginTop: '24px' }}>
          <div className="card-header">
            <h3>Guruhlar statistikasi</h3>
          </div>
          <div className="card-body">
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Guruh nomi</th>
                    <th>Kurs</th>
                    <th>Fanlar</th>
                    <th>Talabalar soni</th>
                  </tr>
                </thead>
                <tbody>
                  {topGroups.map((group) => (
                    <tr key={group.id}>
                      <td><strong>{group.name}</strong></td>
                      <td>{group.course}-kurs</td>
                      <td>{group.subjects?.length || 0} ta</td>
                      <td>
                        <span className="badge badge-blue">
                          {group.students_count} ta
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DashboardHome;
